'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { Video } from '@/payload-types'
import { VideoCardLink } from './VideoCard'

/** Single-row scroller so clips and full-length cards sit on one line at any width. */
export function VideosCarousel({ videos }: { videos: Video[] }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScroll, setCanScroll] = useState(false)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const measure = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScroll(el.scrollWidth > el.clientWidth + 5)
    setIsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 5)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    measure()
    el.addEventListener('scroll', measure)
    window.addEventListener('resize', measure)
    return () => {
      el.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
    }
  }, [measure, videos])

  const scrollBy = (direction: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' })
  }

  return (
    <div>
      <div className="relative">
        <div
          className={`pointer-events-none absolute top-0 right-0 bottom-4 z-10 w-12 transition-opacity duration-300 md:w-16 ${
            canScroll && !isAtEnd ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ background: 'linear-gradient(to left, rgba(245, 245, 240, 0.7), transparent)' }}
        />

        <div
          ref={scrollRef}
          className="scrollbar-hide flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-4 md:gap-6"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {videos.map((video) => (
            <VideoCardLink key={video.id} video={video} layout="carousel" />
          ))}
        </div>
      </div>

      {canScroll && (
        <div className="mt-6 flex items-center gap-2">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-primary transition-colors hover:bg-primary hover:text-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-primary transition-colors hover:bg-primary hover:text-white"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}
